export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            <a href="/" className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-all h-9 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 font-medium">
              Back to Home
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600">Last Updated: September 11, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-gray-50 border-l-4 border-emerald-600 p-6 mb-8 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                We respect your privacy and are committed to protecting it by complying with this privacy policy. This Policy describes your choices regarding our use of your Personal Information and how you can access and update it. <strong>THIS POLICY IS A LEGALLY BINDING AGREEMENT BETWEEN YOU AND THE BUSINESS.</strong> BY ACCESSING AND USING THE PLATFORM, WEBSITE, PRODUCTS AND SERVICES YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THE TERMS OF THIS POLICY.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. Definitions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Unless the context requires otherwise, capitalized terms and expressions in this Agreement are defined terms and expressions which shall have the following meaning:
            </p>

            <div className="space-y-4 mb-8">
              <div>
                <strong className="text-gray-900">1.1. "Agreement"</strong>
                <p className="text-gray-700 ml-4">means the terms and conditions contained herein together with the obligations found in the contents of The Business's: Terms of Use, Disclaimers, User Content Policies, User Contact Policies, Refund Policy, Cookie Policy, DMCA Policy, Accessibility Statement, Shipping Policy, and Affiliate Agreement (if applicable).</p>
              </div>

              <div>
                <strong className="text-gray-900">1.2. "Account"</strong>
                <p className="text-gray-700 ml-4">means any account(s) of or relating to the User for use of the Platform, Website, Products, or Services.</p>
              </div>

              <div>
                <strong className="text-gray-900">1.3. "The Business"</strong>
                <p className="text-gray-700 ml-4">means the entity described at the bottom of the page, having its registered business address at address at the bottom of the page, and includes its subsidiaries and affiliate businesses.</p>
              </div>

              <div>
                <strong className="text-gray-900">1.4. "Personal Information"</strong>
                <p className="text-gray-700 ml-4">means data from you or about you that contains identify features that you may provide or we may collect, on The Business's Platform, Website, Products and Services.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. What Information Do We Collect?</h2>
            
            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2.1. Personal Information You Disclose to Us</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect Personal Information that you voluntarily provide to us when you register/manage an account, express an interest in obtaining information about us or our Products and Services, purchase our Products and Services, or otherwise contact us.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2.2. Personal Information Provided by You</h3>
            <p className="text-gray-700 leading-relaxed mb-4">The Personal Information we collect may include, but is not limited to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Identifier Data:</strong> names, phone numbers, email addresses, mailing addresses, job title, IP Address, usernames, passwords</li>
              <li><strong>Customer Records Data:</strong> billing information, shipping information, purchase history, contact preferences</li>
              <li><strong>Commercial Information:</strong> websites you own/operate, products/services purchased or considered</li>
              <li><strong>Internet Activity Information:</strong> browsing history, search history, interaction with website/applications</li>
              <li><strong>Geolocation Information</strong></li>
              <li><strong>Professional or Employment Related Information</strong></li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2.3. Sensitive Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information: financial data, debit/credit card numbers, banking information, social security numbers or other government identifiers.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. Automatic Collection of Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you visit, use or navigate the Platform, Website, Products and Services, our servers automatically record information that your browser sends. This data may include: your device's IP address, browser type and version, operating system, language preferences, device name, country and location, pages visited, time spent on pages, and other statistics.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. Use and Processing of Collected Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We process your Personal Information for the following reasons:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>To facilitate account creation and authentication</li>
              <li>Personalize the user experience</li>
              <li>To deliver, maintain, improve and facilitate delivery of Products and Services</li>
              <li>To respond to user inquiries/offer support</li>
              <li>To fulfill and manage your orders</li>
              <li>Ensure security and prevent fraud</li>
              <li>To send you marketing and promotional communications</li>
              <li>To deliver targeted advertising</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Privacy of Children</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not knowingly collect any Personal Information from children under the age of 18. Children under the age of 18 are <strong>NOT</strong> authorized to use the Platform, Website, Products and Services. If you are under the age of 18, please do <strong>NOT</strong> use this Platform, Website, Products and Services or submit any Personal Information.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Disclosure of Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To maintain and operate the Platform, Website, Products and Services, we may share your information with third parties including: accounting service providers, analytics providers, cloud computing services, payment processing services, and other service providers performing functions on our behalf.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">7. Cookies and Other Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Platform, Website may use "cookies" to help personalize your online experience. You have the ability to accept or decline cookies. Most web browsers automatically accept cookies by default, but you can modify your browser settings to decline cookies if you prefer.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">8. Information Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any Personal Information we process. However, no electronic transmission over the Internet can be guaranteed to be 100% secure.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">9. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Depending on your location, you may have certain rights including:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Right to access your Personal Information</li>
              <li>Right to correct inaccuracies in your Personal Information</li>
              <li>Right to request deletion of your Personal Information</li>
              <li>Right to obtain a copy of your Personal Information</li>
              <li>Right to opt out of certain processing activities</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">10. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify this Policy at any time. We will alert you about any changes by updating the "Last Updated" date of this Policy. Your continued use of the Platform, Website, Products or Services after the effective date of the revised Policy will constitute your consent to those changes.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">11. Contact Information</h2>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mt-6">
              <p className="text-gray-700 mb-2"><strong>Business Name:</strong> InstaVisible Tax & Savings Solutions</p>
              <p className="text-gray-700 mb-2"><strong>Email:</strong> contact@instavisible.com</p>
              <p className="text-gray-700"><strong>Website:</strong> www.instavisible.com</p>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Copyright © 2025 Compliance Media LLC. Licensed to ESTAGE LLC. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
            <div className="flex items-center gap-6 sm:gap-8 text-white/80 flex-wrap justify-center">
              <a href="/privacy-policy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="/terms-of-use" className="hover:text-white transition-colors whitespace-nowrap">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
